import React, { useEffect, useMemo, useState } from "react";
import { BarChart, DonutChart, SegmentedBar, niceAxis, pickUnit } from "../../components/Chart";
import usePostData from "../../hooks/usePostData";
import { TokenService } from "../../services/Api";
import styles from "./DashboardHome.module.css";

/* ══════════════ Endpoints ══════════════ */
const API = {
  overview: "/saas/dashboard/business-overview",
  revenue: "/saas/dashboard/revenue-growth",
  plans: "/saas/dashboard/schools-by-plan",
  roles: "/saas/dashboard/users-by-role",
  activity: "/saas/dashboard/user-activity",
  installs: "/saas/dashboard/app-installs",
  storage: "/saas/sync/db-sizes",
};

/* user-activity and app-installs are scoped to the signed-in school. */
const currentClientId = () =>
  TokenService.getUser()?.client_data?.[0]?.client_id ?? "";

/* ══════════════ Formatters ══════════════ */
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const formatCount = (n) => Number(n || 0).toLocaleString("en-IN");

/* ══════════════ Business overview cards ══════════════ */
const CARD_META = {
  total_schools: { label: "Total schools", icon: "school", tint: "red" },
  active_schools: { label: "Active schools", icon: "apartment", tint: "blue" },
  revenue: { label: "Quarterly Revenue", icon: "currency_rupee", tint: "green" },
  new_schools: { label: "New schools", icon: "person_add", tint: "violet" },
  demo_requests: { label: "Open demo requests", icon: "chat_bubble", tint: "orange", noteTone: "orange" },
  in_discussion: { label: "Schools in discussion", icon: "warning", tint: "amber", noteTone: "amber", note: "In sales pipeline" },
  renewed_schools: { label: "Renewed schools", icon: "autorenew", tint: "green", noteTone: "green" },
  inactive_schools: { label: "Inactive schools", icon: "block", tint: "red" },
};
const CARD_ORDER = [
  "total_schools", "active_schools", "revenue", "new_schools",
  "demo_requests", "in_discussion", "renewed_schools", "inactive_schools",
];
const EMPTY_CARDS = CARD_ORDER.map((key) => ({ ...CARD_META[key], value: "—" }));

const buildStatCards = (cards) => {
  const byKey = Object.fromEntries((cards || []).map((c) => [c.key, c]));
  return CARD_ORDER.map((key) => {
    const meta = CARD_META[key];
    const c = byKey[key] || {};

    let value;
    if (key === "revenue") value = formatINR(c.value);
    else if (key === "new_schools") value = `+${Number(c.value || 0)}`;
    else value = String(c.value ?? 0);

    /* Either a percentage or an absolute delta, whichever the API sent. */
    const change = c.change_pct ?? c.change;
    const isPct = c.change_pct != null;
    const delta =
      change != null && c.compare_label
        ? `${change >= 0 ? "+" : ""}${change}${isPct ? "%" : ""} vs ${c.compare_label}`
        : undefined;

    return {
      ...meta,
      value,
      delta,
      up: delta ? c.direction !== "down" : undefined,
      note: c.subtitle ?? meta.note,
    };
  });
};

/* ══════════════ Users by role ══════════════ */
const ADMIN_ROLES = new Set(["Group Administrator"]);
const ROLE_LABELS = { Parent: "Parents", "Student account": "Students", Staff: "Staff" };
const ROLE_COLOR = {
  Students: "var(--chart-series-1)",
  Parents: "var(--chart-series-2)",
  Staff: "var(--chart-series-3)",
  Admins: "var(--chart-series-4)",
};

/* API roles → segments, with every admin-ish role folded into one "Admins" row. */
const buildRoleSegments = (roles) => {
  const segments = [];
  let adminUsers = 0;
  let adminPct = 0;

  (roles || []).forEach((r) => {
    if (ADMIN_ROLES.has(r.role_name)) {
      adminUsers += r.total_users;
      adminPct += r.percentage;
    } else if (r.total_users > 0) {
      const label = ROLE_LABELS[r.role_name] || r.role_name;
      segments.push({
        label,
        pct: r.percentage,
        caption: `${r.total_users_formatted} users`,
        color: ROLE_COLOR[label],
      });
    }
  });

  if (adminUsers > 0) {
    segments.push({
      label: "Admins",
      pct: adminPct,
      caption: `${formatCount(adminUsers)} users`,
      color: ROLE_COLOR.Admins,
    });
  }
  return segments;
};

/* ══════════════ Chart series ══════════════ */
const REVENUE_SERIES = [
  { key: "newBusiness", label: "New Business Revenue", tone: "green" },
  { key: "renewal", label: "Renewal Revenue", tone: "blue" },
];
const INSTALL_SERIES = [
  { key: "android", label: "Android", tone: "blue" },
  { key: "ios", label: "iOS", tone: "violet" },
];

/* ══════════════ Storage ══════════════ */
/* The db-sizes API reports used storage only — allocated capacity lives here. */
const STORAGE_ALLOCATED_MB = 14 * 1024 * 1024; // 14 TB
const STORAGE_ALLOCATED_LABEL = "14.0 TB";

/* ══════════════ Filters ══════════════ */
const AY_OPTIONS = [
  { value: "2026-2027", label: "2026 - 2027" },
  { value: "2025-2026", label: "2025 - 2026" },
  { value: "2024-2025", label: "2024 - 2025" },
];
const PERIOD_TYPE_OPTIONS = [
  { value: "quarterly", label: "Quarterly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];
/* Shared by user-activity and app-installs — the API takes the numeric value. */
const RANGE_OPTIONS = [
  { value: "0", label: "Last day" },
  { value: "1", label: "Last 7 days" },
  { value: "2", label: "Last 15 days" },
  { value: "3", label: "Last 1 month" },
  { value: "4", label: "Last 3 months" },
  { value: "5", label: "Last 6 months" },
];
const DEFAULT_RANGE = "5";

/* client_id always goes on the wire — "" when there is no client on the session. */
const rangeBody = (clientId, range) => ({
  client_id: clientId || "",
  filter: Number(range),
});

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/* "2026-09" → "Sep 2026" */
const monthLabel = (month) => {
  const [year, m] = String(month || "").split("-").map(Number);
  return MONTHS[m - 1] ? `${MONTHS[m - 1]} ${year}` : String(month || "");
};
const lastDay = (y, m0) => new Date(y, m0 + 1, 0).getDate();
const ymd = (y, m0, d) => `${y}-${String(m0 + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

/* An academic year runs Apr (start year) → Mar (end year). */
const academicMonths = (ay) => {
  const [startYear, endYear] = ay.replace(/\s/g, "").split("-").map(Number);
  const months = [];
  for (let m = 3; m <= 11; m++) months.push({ year: startYear, month: m });
  for (let m = 0; m <= 2; m++) months.push({ year: endYear, month: m });
  return months;
};

const buildPeriodOptions = (ay, periodType) => {
  const months = academicMonths(ay);
  const range = (first, last) => ({
    label: `${MONTHS[first.month]} ${first.year} - ${MONTHS[last.month]} ${last.year}`,
    start: ymd(first.year, first.month, 1),
    end: ymd(last.year, last.month, lastDay(last.year, last.month)),
  });

  if (periodType === "monthly") {
    return months.map((m) => range(m, m));
  }
  if (periodType === "yearly") {
    return [range(months[0], months[months.length - 1])];
  }
  const quarters = [];
  for (let i = 0; i < months.length; i += 3) quarters.push(range(months[i], months[i + 2]));
  return quarters;
};

const DEFAULT_AY = "2026-2027";
const DEFAULT_PERIOD_TYPE = "quarterly";
const [DEFAULT_PERIOD] = buildPeriodOptions(DEFAULT_AY, DEFAULT_PERIOD_TYPE);
const DEFAULT_FILTER = {
  academic_year: DEFAULT_AY,
  period_type: DEFAULT_PERIOD_TYPE,
  period_start: DEFAULT_PERIOD.start,
  period_end: DEFAULT_PERIOD.end,
};

/* ══════════════ Atoms ══════════════ */
function StatCard({ item }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statTop}>
        <span className={styles.statLabel}>{item.label}</span>
        <span className={`${styles.statIcon} ${styles["tint" + cap(item.tint)]}`}>
          <span className="material-symbols-outlined">{item.icon}</span>
        </span>
      </div>
      <div className={styles.statValue}>{item.value}</div>
      {item.delta && (
        <div className={`${styles.statDelta} ${item.up === false ? styles.deltaDown : styles.deltaUp}`}>
          {item.up === false ? "▼" : "▲"} {item.delta}
        </div>
      )}
      {item.note && (
        <div className={`${styles.statNote} ${item.noteTone ? styles["note" + cap(item.noteTone)] : ""}`}>
          {item.note}
        </div>
      )}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select className={styles.select} value={value} onChange={(e) => onChange?.(e.target.value)}>
      {options.map((o) =>
        typeof o === "string"
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>,
      )}
    </select>
  );
}

function Panel({ title, action, children }) {
  return (
    <div className={styles.card}>
      <div className={styles.cardHeaderRow}>
        <div className={styles.cardTitle}>{title}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function TotalUsers({ value }) {
  return (
    <div className={styles.totalInline}>
      <span className={styles.totalLabel}>Total Users</span>
      <span className={styles.totalValue}>{value}</span>
    </div>
  );
}

/* ══════════════ Dashboard ══════════════ */
export default function Dashboard() {
  /* ── Filters ── */
  const [academicYear, setAcademicYear] = useState(DEFAULT_AY);
  const [periodType, setPeriodType] = useState(DEFAULT_PERIOD_TYPE);
  const [periodIdx, setPeriodIdx] = useState(0);
  const [appliedFilter, setAppliedFilter] = useState(DEFAULT_FILTER);

  const periodOptions = useMemo(
    () => buildPeriodOptions(academicYear, periodType),
    [academicYear, periodType],
  );
  useEffect(() => setPeriodIdx(0), [academicYear, periodType]);

  const stagedFilter = useMemo(() => {
    const option = periodOptions[periodIdx] || periodOptions[0];
    return {
      academic_year: academicYear,
      period_type: periodType,
      period_start: option?.start,
      period_end: option?.end,
    };
  }, [academicYear, periodType, periodIdx, periodOptions]);

  /* schools-by-plan only accepts the period window. */
  const planFilter = useMemo(
    () => ({ period_start: appliedFilter.period_start, period_end: appliedFilter.period_end }),
    [appliedFilter],
  );

  /* ── Per-widget range filters (own selects, applied immediately) ── */
  const [activityRange, setActivityRange] = useState(DEFAULT_RANGE);
  const [installRange, setInstallRange] = useState(DEFAULT_RANGE);

  const clientId = useMemo(() => currentClientId(), []);
  const activityFilter = useMemo(
    () => rangeBody(clientId, activityRange),
    [clientId, activityRange],
  );
  const installFilter = useMemo(
    () => rangeBody(clientId, installRange),
    [clientId, installRange],
  );

  /* ── Data ── */
  const overview = usePostData(API.overview, appliedFilter);
  const revenue = usePostData(API.revenue, appliedFilter);
  const plans = usePostData(API.plans, planFilter);
  const roles = usePostData(API.roles);
  const activity = usePostData(API.activity, activityFilter);
  const installs = usePostData(API.installs, installFilter);
  const storage = usePostData(API.storage);

  /* ── Business overview ── */
  const statCards = overview.data ? buildStatCards(overview.data.cards) : EMPTY_CARDS;

  /* ── Revenue growth: plot in K / L / Cr so the axis stays readable ── */
  const revenueChart = useMemo(() => revenue.data?.chart || [], [revenue.data]);
  const revenueAxis = useMemo(() => {
    const maxTotal = Math.max(0, ...revenueChart.map((r) => r.total_revenue || 0));
    const { div, suffix } = pickUnit(maxTotal);
    const { max, step } = niceAxis(maxTotal / div);
    return { div, suffix, max, step };
  }, [revenueChart]);

  const revenueRows = revenueChart.map((r) => ({
    label: r.label,
    newBusiness: +((r.new_revenue || 0) / revenueAxis.div).toFixed(2),
    renewal: +((r.renewal_revenue || 0) / revenueAxis.div).toFixed(2),
  }));

  /* ── Users by role ── */
  const roleSegments = buildRoleSegments(roles.data?.roles);
  const totalUsers = roles.data?.total?.total_users_formatted ?? "0";

  /* ── User activity ── */
  const activityTotal = activity.data?.total_users ?? 0;
  const activeUsers = activity.data?.active_users ?? 0;
  const inactiveUsers = activity.data?.not_active_users ?? 0;
  const activitySegments = activityTotal
    ? [
      { label: "Active", pct: activity.data?.active_percent ?? 0, caption: `${formatCount(activeUsers)} users`, color: ROLE_COLOR.Students },
      { label: "Not Active", pct: activity.data?.not_active_percent ?? 0, caption: `${formatCount(inactiveUsers)} users`, color: ROLE_COLOR.Admins },
    ]
    : [];

  /* ── App installs by platform ── */
  const installChart = useMemo(() => installs.data?.chart || [], [installs.data]);
  const installAxis = useMemo(() => {
    const maxTotal = Math.max(0, ...installChart.map((r) => r.total || 0));
    const { div, suffix } = pickUnit(maxTotal);
    const { max, step } = niceAxis(maxTotal / div);
    return { div, suffix, max, step };
  }, [installChart]);

  const installRows = installChart.map((r) => ({
    label: monthLabel(r.month),
    android: +((r.android || 0) / installAxis.div).toFixed(2),
    ios: +((r.ios || 0) / installAxis.div).toFixed(2),
  }));

  /* ── Schools by plan ── */
  const planSegments = (plans.data?.segments || []).map((s) => ({
    label: s.label,
    value: s.school_count,
    caption: `${s.school_count} ${s.school_count === 1 ? "school" : "schools"}`,
    captionSub: `(${formatINR(s.revenue)})`,
  }));

  /* ── Storage utilization ── */
  const storageUsedMb = storage.data?.total_size_mb || 0;
  const storageUsedLabel = storage.data?.total_size_formatted || "0 MB";
  const storagePct = Math.min(100, (storageUsedMb / STORAGE_ALLOCATED_MB) * 100);
  const storagePctLabel = storagePct > 0 && storagePct < 1 ? "<1%" : `${Math.round(storagePct)}%`;
  const topConsumers = [...(storage.data?.list || [])]
    .sort((a, b) => (b.size_mb || 0) - (a.size_mb || 0))
    .slice(0, 5);

  return (
    <div className={styles.page}>
      <div className={styles.dash}>
        <div className={styles.crumb}>Dashboard</div>
        <h1 className={styles.title}>Dashboard</h1>

        {/* ── Business overview + revenue ── */}
        <div className={styles.filterCard}>
          <div className={styles.boHeaderRow}>
            <div>
              <div className={styles.boTitle}>Business Overview</div>
              <div className={styles.boSubtitle}>Showing data for all widgets below</div>
            </div>
            <div className={styles.filterRow}>
              <span className={styles.filterLabel}>Filter by:</span>
              <Select value={academicYear} onChange={setAcademicYear} options={AY_OPTIONS} />
              <Select value={periodType} onChange={setPeriodType} options={PERIOD_TYPE_OPTIONS} />
              <Select
                value={String(periodIdx)}
                onChange={(v) => setPeriodIdx(Number(v))}
                options={periodOptions.map((o, i) => ({ value: String(i), label: o.label }))}
              />
              <button className={styles.applyBtn} onClick={() => setAppliedFilter(stagedFilter)}>
                Apply Filters
              </button>
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.statsGrid}>
              {overview.loading && <div className={styles.gridLoading}>Loading…</div>}
              {statCards.map((s) => <StatCard key={s.label} item={s} />)}
            </div>
          </div>

          <div className={styles.card}>
            <BarChart
              title="Revenue growth"
              stacked
              data={revenueRows}
              series={REVENUE_SERIES}
              loading={revenue.loading}
              error={revenue.error}
              emptyText="No revenue for this period."
              maxY={revenueAxis.max}
              stepSize={revenueAxis.step}
              valueSuffix={revenueAxis.suffix}
            />
          </div>
        </div>

        {/* ── Users by role ── */}
        <Panel title="Users by role" action={<TotalUsers value={totalUsers} />}>
          <SegmentedBar
            segments={roleSegments}
            showPercent
            loading={roles.loading}
            error={roles.error}
            emptyText="No user data."
          />
        </Panel>

        {/* ── User activity ── */}
        <Panel
          title="User Activity"
          action={<Select value={activityRange} onChange={setActivityRange} options={RANGE_OPTIONS} />}
        >
          <div className={styles.activityTotalRow}>
            <span className={styles.totalLabel}>Total Users</span>
            <span className={styles.totalValue}>{formatCount(activityTotal)}</span>
          </div>
          <SegmentedBar
            segments={activitySegments}
            legendLayout="row"
            loading={activity.loading}
            error={activity.error}
            emptyText="No activity data."
          />
        </Panel>

        {/* ── App installs ── */}
        <div className={styles.card}>
          <BarChart
            title="App installs by platform"
            stacked
            data={installRows}
            series={INSTALL_SERIES}
            loading={installs.loading}
            error={installs.error}
            emptyText="No installs for this period."
            maxY={installAxis.max}
            stepSize={installAxis.step}
            valueSuffix={installAxis.suffix}
            actions={<Select value={installRange} onChange={setInstallRange} options={RANGE_OPTIONS} />}
          />
        </div>

        {/* ── Plan mix + storage ── */}
        <div className={styles.donutGrid}>
          <div className={styles.card}>
            <DonutChart
              title="Schools by plan (with revenue)"
              subtitle="Schools count vs revenue share"
              segments={planSegments}
              centerValue={plans.data?.total_schools ?? 0}
              centerLabel="Total Schools"
              loading={plans.loading}
              error={plans.error}
              emptyText="No plan data."
            />
          </div>

          <div className={styles.card}>
            <div className={styles.storageHead}>
              <div>
                <div className={styles.cardTitle}>Storage utilization</div>
                <div className={styles.storageSub}>
                  {storageUsedLabel} used of {STORAGE_ALLOCATED_LABEL} allocated
                </div>
              </div>
              <div className={styles.storagePct}>{storagePctLabel}</div>
            </div>

            <SegmentedBar
              variant="meter"
              showLegend={false}
              minVisiblePct={0.5}
              segments={[{ label: "Used", pct: storagePct, color: "var(--gradient-orange)" }]}
            />

            <div className={styles.storageConsumersLabel}>TOP 5 STORAGE CONSUMERS</div>
            {storage.loading ? (
              <div className={styles.muted}>Loading…</div>
            ) : topConsumers.length === 0 ? (
              <div className={styles.muted}>{storage.error || "No storage data."}</div>
            ) : (
              topConsumers.map((c) => (
                <div key={c.db_name} className={styles.storageRow}>
                  <span className={styles.storageName}>{c.school_name}</span>
                  <span className={styles.storageSize}>{c.size_formatted}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
